import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EntityList from '../components/EntityList/EntityList';
import ChipBuilder from '../components/ChipBuilder';
import AddButton from '../components/AddButton/AddButton';
import { ExportCSVButton } from '../components/ExportButtons';
import { listGolfCourses, archiveGolfCourse, updateGolfCourse, isAdmin as checkAdmin, exportGolfCoursesCSV } from '../api';
import { LIST_PAGE_SIZE } from '../constants';
import useEntityList from '../hooks/useEntityList';
import styles from './GolfCoursesPage.module.css';

const COLUMNS = [
  {
    key: 'name',
    label: 'Course name',
  },
  {
    key: 'holes',
    label: 'Holes',
    render: (value) => (
      <span className={styles.holesBadge}>
        {value}H
      </span>
    ),
  },
  {
    key: 'allows_carts',
    label: 'Allows carts',
    render: (value) => (value ? 'Yes' : 'No'),
  },
  {
    key: 'members_only',
    label: 'Members only',
    render: (value) => (value ? 'Yes' : 'No'),
  },
];

const CHIP_FIELDS = [
  { key: 'name',  label: 'Name',  type: 'text',   placeholder: 'Course name…' },
  { key: 'holes', label: 'Holes', type: 'select',
    options: [{ value: '9', label: '9 holes' }, { value: '18', label: '18 holes' }] },
  { key: 'is_archived', label: 'Archived', type: 'select',
    options: [{ value: 'false', label: 'Active' }, { value: 'true', label: 'Archived' }] },
];

export default function GolfCoursesPage({ user }) {
  const navigate = useNavigate();
  const isAdmin = checkAdmin(user);

  const [chips, setChips] = useState([]);
  const [exportError, setExportError] = useState('');

  // Derive archived state from chips (server-side filter)
  const isArchived = useMemo(
    () => chips.find((c) => c.field === 'is_archived')?.value === 'true',
    [chips]
  );

  const filters = useMemo(() => ({ isArchived }), [isArchived]);

  const fetchFn = useCallback((cursor, { isArchived: archived }) =>
    listGolfCourses({ isArchived: archived, cursor, limit: LIST_PAGE_SIZE }),
  []);

  const {
    items: rawItems,
    total,
    loading,
    fetchError,
    selectedIds,
    setSelectedIds,
    page,
    totalPages,
    handleNext,
    handlePrev,
    handleArchive,
    handleUnarchive,
  } = useEntityList({
    fetchFn,
    archiveFn: archiveGolfCourse,
    unarchiveFn: (id) => updateGolfCourse(id, { is_archived: false }),
    filters,
  });

  // Client-side chip filtering (name, holes — is_archived is server-side)
  const filteredItems = useMemo(() => {
    let result = rawItems;
    chips.forEach((chip) => {
      if (chip.field === 'name') {
        const lower = chip.value.toLowerCase();
        result = result.filter((item) => item.name.toLowerCase().includes(lower));
      }
      if (chip.field === 'holes') {
        result = result.filter((item) => String(item.holes) === chip.value);
      }
      // is_archived handled server-side — skip client-side
    });
    return result;
  }, [rawItems, chips]);

  const exportParams = {
    isArchived,
    name: chips.find((c) => c.field === 'name')?.value,
    holes: chips.find((c) => c.field === 'holes')?.value,
  };

  const addButton = (
    <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
      <ExportCSVButton
        exportFn={() => exportGolfCoursesCSV(exportParams)}
        onError={setExportError}
        variant="list"
      />
      {isAdmin && <AddButton label="Add golf course" onClick={() => navigate('/golf-courses/new')} />}
    </div>
  );

  const filterPanel = (
    <div>
      <ChipBuilder
        chips={chips}
        onChipsChange={setChips}
        fields={CHIP_FIELDS}
      />
      {exportError && <p className={styles.exportError}>{exportError}</p>}
    </div>
  );

  return (
    <EntityList
      title="Golf courses"
      columns={COLUMNS}
      items={filteredItems}
      total={total}
      loading={loading}
      errorMessage={fetchError}
      onRowClick={(item) => navigate(`/golf-courses/${item.id}`)}
      isAdmin={isAdmin}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      onArchive={handleArchive}
      filterPanel={filterPanel}
      page={page}
      totalPages={totalPages}
      onPrev={handlePrev}
      onNext={handleNext}
      addButton={addButton}
      showArchived={isArchived}
      onUnarchive={handleUnarchive}
    />
  );
}
